import * as React from 'react';
import { useState, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import {
  Box,
  Button,
  Container,
  List,
  Pagination,
  PaginationItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Modal,
  TextField,
  IconButton,
  Card,
  CardContent,
  Grid,
  Paper,
  Chip,
  Tooltip,
  Divider,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Switch,
  FormControlLabel
} from '@mui/material';
import { createTheme, ThemeProvider, styled } from '@mui/material/styles';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person,
  Business,
  Phone,
  Email,
  AccessTime,
  CheckCircle,
  Pending,
  Cancel,
  Badge,
  Lock
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom'
import axios from 'axios';
import { PAGE_SIZE, url, roleLabels } from '../utils/constants';
import { LoaderOverlay } from '../utils/components';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const theme = createTheme();

// Styled components para elementos modernos
const ModernButton = styled(Button)(({ theme }) => ({
  borderRadius: '25px',
  textTransform: 'none',
  fontWeight: 600,
  padding: '12px 32px',
  fontSize: '1.1rem',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
  },
}));

const ModernCard = styled(Card)(({ theme }) => ({
  borderRadius: '20px',
  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 15px 40px rgba(0,0,0,0.15)',
  },
}));

const ModernTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.9)',
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#1976D2',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#1976D2',
      borderWidth: 2,
    },
  },
  '& .MuiInputLabel-root': {
    color: '#666',
    '&.Mui-focused': {
      color: '#1976D2',
    },
  },
}));


export default function PerfilList({ usePageTitle }) {
  usePageTitle('PixTick | Perfis');
  const [open, setOpen] = useState(false);
  const [selectedPerfil, setSelectedPerfil] = useState(null);
  const [perfis, setPerfis] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser, permissions, logout } = useAuth();
  const canEditProfiles = currentUser?.role === 'SUPER_ADMIN' || permissions.canEditProfiles !== false;
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const initialPerfilRef = React.useRef(null);
  const [filters, setFilters] = useState({
    descricao: '',
    nome: '',
    dataInicio: '',
    dataFim: '',
    telefone: '',
    id: '',
    idTransacao: '',
    idpdvs: [],
    status: 'ATIVO', // Por padrão, mostrar apenas usuários ativos
    geral: ''
  });

  const handleOpen = (perfil) => {
    setSelectedPerfil({ ...perfil });
    initialPerfilRef.current = perfil ? { ...perfil } : null;
    setPasswordConfirm('');
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedPerfil(null);
    initialPerfilRef.current = null;
    setPasswordConfirm('');
  };

  const handleEdit = (perfil) => {
    handleOpen(perfil);
  };

  const handleDelete = async (id) => {
    try {
      // Confirmar exclusão
      const confirmDelete = window.confirm(
        'Tem certeza que deseja excluir este usuário?\n\n' +
        'Esta ação irá desativar o usuário (exclusão lógica) e ele não poderá mais fazer login.\n' +
        'Os dados das transações serão preservados.'
      );

      if (!confirmDelete) {
        return;
      }

      setLoading(true);

      const token = localStorage.getItem('token');

      if (!token) {
        toast.error("Token não encontrado!", {
          description: "Faça login novamente",
          duration: 4000,
        });
        logout();
        navigate('/signin');
        return;
      }

      // Fazer a requisição para alterar o status para INATIVO
      const response = await axios.post(
        url + `/api/v1/user/atualizaStatus/${id}`,
        { status: 'INATIVO' },
        {
          headers: {
            'Authorization': 'Bearer ' + token
          }
        }
      );

      console.log('Resposta da API:', response.data);

      // Atualizar a lista de perfis localmente
      setPerfis((prevPerfis) =>
        prevPerfis.map((perfil) =>
          perfil._id === id ? { ...perfil, status: 'INATIVO' } : perfil
        )
      );

      toast.success("Usuário excluído com sucesso!", {
        description: "O usuário foi desativado (exclusão lógica)",
        duration: 4000,
      });

      // Recarregar a lista para garantir sincronização
      fetchData();

    } catch (error) {
      console.error("Erro ao excluir usuário", error);
      console.error('Status:', error.response?.status);
      console.error('Data:', error.response?.data);

      if (error.response?.status === 401) {
        toast.error("Sessão expirada!", {
          description: "Faça login novamente para continuar",
          duration: 5000,
        });
        logout();
        navigate('/signin');
      } else {
        toast.error("Erro ao excluir usuário!", {
          description: error.response?.data?.message || "Tente novamente mais tarde",
          duration: 4000,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedPerfil) return;

    const roleAlterado = currentUser?.role === 'SUPER_ADMIN' && initialPerfilRef.current?.role !== selectedPerfil?.role;
    if (roleAlterado && !passwordConfirm?.trim()) {
      toast.error('Confirme sua senha', {
        description: 'Para alterar o nível do usuário, digite sua senha.',
        duration: 4000,
      });
      return;
    }

    try {
      setLoading(true);

      const token = localStorage.getItem('token');

      if (!token) {
        toast.error("Token não encontrado!", {
          description: "Faça login novamente",
          duration: 4000,
        });
        logout();
        navigate('/signin');
        return;
      }

      const { email, ...rest } = selectedPerfil;
      const dadosPermitidos = { ...rest };
      if (currentUser?.role !== 'SUPER_ADMIN') delete dadosPermitidos.role;

      const payload = {
        user: { email: selectedPerfil.email },
        novosDados: dadosPermitidos
      };
      if (currentUser?.role === 'SUPER_ADMIN' && passwordConfirm?.trim()) {
        payload.passwordConfirm = passwordConfirm;
      }

      const response = await axios.post(url + `/api/v1/user/atualizaDadosPerfil`, payload, {
        headers: { 'Authorization': 'Bearer ' + token }
      });

      console.log('Resposta da API:', response.data);

      // Atualiza a lista de perfis após a edição
      setPerfis((prevPerfis) =>
        prevPerfis.map((perfil) =>
          perfil.email === selectedPerfil.email ? { ...perfil, ...selectedPerfil } : perfil
        )
      );

      toast.success("Perfil atualizado com sucesso!", {
        description: "As alterações foram salvas",
        duration: 3000,
      });

      handleClose();
    } catch (error) {
      console.error("Erro ao salvar alterações", error);
      console.error('Status:', error.response?.status);
      console.error('Data:', error.response?.data);

      if (error.response?.status === 401) {
        toast.error("Sessão expirada!", {
          description: "Faça login novamente para continuar",
          duration: 5000,
        });

        // Limpar dados de autenticação
        localStorage.removeItem('token');
        localStorage.removeItem('fullname');
        localStorage.removeItem('role');
        localStorage.removeItem('email');

        setTimeout(() => {
          logout();
          navigate('/signin');
        }, 2000);
      } else {
        const errorMessage = error.response?.data?.message || "Erro ao salvar alterações";
        toast.error("Erro ao salvar alterações", {
          description: errorMessage,
          duration: 5000,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const page = parseInt(queryParams.get('page')) || 1;
    setCurrentPage(page);
  }, [location.search]);

  const fetchData = async () => {
    try {
      const token = await localStorage.getItem('token');
      const queryParams = new URLSearchParams({
        page: currentPage,
        ...filters,
        status: filters.status === 'Todos' ? '' : filters.status
      });

      const response = await axios.get(url + `/api/v1/user/perfis?${queryParams}`, {
        headers: ({
          Authorization: 'Bearer ' + token
        })
      });
      const { data, totalcount } = response.data;

      console.log(totalcount);

      setPerfis(data);

      setTotalPages(Math.ceil(totalcount / PAGE_SIZE));

      //console.log(data);


      // Dados carregados com sucesso
    } catch {
      logout();
      navigate('/signin');
    } finally {
      setLoading(false);
      //console.log(transactions);

    }
  };

  // Recarregar dados quando o filtro de status mudar
  useEffect(() => {
    if (filters.status) {
      fetchData();
    }
  }, [filters.status]);

  useEffect(() => {
    fetchData();
  }, [navigate, logout, currentPage, filters]);

  const handlePageChange = (event, pageNumber) => {
    setCurrentPage(pageNumber);

    navigate(`?page=${pageNumber}`);
  };

  // Aplicar máscara ao CNPJ
  const formatCNPJ = (value) => {
    return value
      .replace(/\D/g, '') // Remove tudo que não for número
      .replace(/^(\d{2})(\d)/, '$1.$2') // Adiciona ponto após os 2 primeiros números
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3') // Adiciona ponto após os próximos 3 números
      .replace(/\.(\d{3})(\d)/, '.$1/$2') // Adiciona barra após os próximos 3 números
      .replace(/(\d{4})(\d)/, '$1-$2') // Adiciona traço antes dos últimos 2 números
      .slice(0, 18); // Garante que não ultrapasse 18 caracteres (formato do CNPJ)
  };

  function formatPhoneNumber(phoneNumber) {
    if (!phoneNumber) return phoneNumber;

    // Definir máscaras para cada DDI
    const masks = {
      '+55': { name: 'Brasil', mask: '(xx) xxxxx-xxxx' },
      '+54': { name: 'Argentina', mask: '(x) xxxx xx-xxxx' },
      '+595': { name: 'Paraguai', mask: '(xxx) xxx-xxx' },
      '+1': { name: 'EUA', mask: '(xxx) xxx-xxxx' }
      // Adicione outras máscaras conforme necessário
    };

    // Detectar o DDI do número de telefone
    let ddi = Object.keys(masks).find(ddi => phoneNumber.startsWith(ddi));
    if (!ddi) return phoneNumber; // Se não for encontrado nenhum DDI correspondente, retorne o número de telefone sem formatação
    phoneNumber = phoneNumber.replace(ddi, ''); // Remover o DDI do número de telefone
    // Obter a máscara correspondente ao DDI detectado
    const mask = masks[ddi].mask;

    // Remover caracteres não numéricos do número de telefone
    const numericPhoneNumber = phoneNumber.replace(/\D/g, '');

    // Aplicar máscara ao número de telefone
    let formattedPhoneNumber = '';
    let maskIndex = 0;
    for (let i = 0; i < mask.length && maskIndex < numericPhoneNumber.length; i++) {
      if (mask[i] === 'x') {
        formattedPhoneNumber += numericPhoneNumber[maskIndex];
        maskIndex++;
      } else {
        formattedPhoneNumber += mask[i];
      }
    }
    //ddi + formattedPhoneNumber;

    return ddi + ' ' + formattedPhoneNumber;
  }

  const formatCurrency = (value) => {
    if (!value) return value;

    // Formata o valor em moeda brasileira (pt-BR)
    return parseFloat(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  function getStatusColor(status) {
    switch (status) {
      case 'EMANALISE':
        return '#0000FF'; // Amarelo
      case 'ATIVO':
        return '#008000'; // Verde
      case 'Concluído':
        return '#0000FF'; // Azul
      case 'Falhou':
        return '#FF0000'; // Vermelho
      default:
        return '#FF0000'; // Cinza (ou qualquer outra cor padrão)
    }
  }

  const handlePerfilClick = (perfil) => {
    console.log(perfil);
    navigate(`/perfil-detalhado/${perfil._id}`)
    //setSelectedTransaction(transaction);
  };


  // const handleFilter = async () => {
  //   setLoading(true);
  //   setCurrentPage(1);

  //   try {
  //     const token = await localStorage.getItem('token');
  //     const queryParams = new URLSearchParams({
  //       page: currentPage,
  //       ...filters,
  //       idpdvs: filters.idpdvs ? filters.idpdvs.join(',') : '',
  //       status: filters.status === 'Todos' ? '' : filters.status
  //     });
  //     console.log('teste> ' + filters.geral);
  //     const response = await axios.get(url + `/api/v1/user/perfis?${queryParams}`, {
  //       headers: ({
  //         Authorization: 'Bearer ' + token
  //       })
  //     });
  //     const { data, totalcount } = response.data;

  //     setPerfis(data);
  //     setTotalPages(Math.ceil(totalcount / PAGE_SIZE));

  //     //console.log(data);

  //     setIsLoggedIn(true);
  //   } catch {
  //     setIsLoggedIn(false);
  //     navigate('/signin');
  //   } finally {

  //     setLoading(false);
  //   };
  // }




  const handleChangeStatus = async (perfil) => {
    const confirmChange = window.confirm(`Tem certeza que deseja ${perfil.status === 'ATIVO' ? 'desativar' : 'ativar'} este perfil?`);

    if (!confirmChange) return; // Se o usuário cancelar, não prosseguir.

    setLoading(true);
    try {
      const token = await localStorage.getItem('token');
      const statusTemp = perfil.status;
      const newStatus = perfil.status === 'ATIVO' ? 'INATIVO' : perfil.status === 'INATIVO' ? 'ATIVO' : perfil.status;

      if (statusTemp !== newStatus) {
        await axios.post(url + `/api/v1/user/atualizaStatus/${perfil._id}`, { status: newStatus }, {
          headers: {
            Authorization: 'Bearer ' + token
          }
        });

        // Atualizar a lista localmente
        setPerfis((prevPerfis) =>
          prevPerfis.map((p) =>
            p._id === perfil._id ? { ...p, status: newStatus } : p
          )
        );

        toast.success(`Usuário ${newStatus === 'ATIVO' ? 'ativado' : 'desativado'} com sucesso!`, {
          description: `Status alterado para ${newStatus}`,
          duration: 3000,
        });

        // Recarregar a lista para garantir sincronização e aplicar filtros
        fetchData();
      }
    } catch (error) {
      console.error("Erro ao alterar status", error);
      toast.error("Erro ao alterar status!", {
        description: "Tente novamente mais tarde",
        duration: 4000,
      });
      navigate('/signin');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{
        minHeight: '100vh',
        backgroundColor: '#FFFFFA',
        padding: { xs: 0.5, md: 2 },
        paddingTop: { xs: 8, md: 10 },
        width: '100%',
        margin: 0
      }}>
        <Container maxWidth="xl" sx={{ width: '100%', px: { xs: 0.5, md: 2 } }}>
          {loading ? (
            <LoaderOverlay loading={loading} />
          ) : (
            <Grid container spacing={{ xs: 1.5, md: 2 }}>
              {/* Título da página */}
              <Grid item xs={12}>
                <Box sx={{
                  backgroundColor: 'white',
                  borderRadius: 3,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  padding: { xs: 2, md: 3 },
                  textAlign: 'center',
                  mb: 2
                }}>
                  <Typography
                    variant="h4"
                    component="h1"
                    sx={{
                      fontWeight: 700,
                      fontFamily: 'Ubuntu, sans-serif',
                      color: '#1976D2',
                      fontSize: { xs: '1.8rem', md: '2.5rem' },
                      lineHeight: 1.2
                    }}
                  >
                    Perfis de Usuários
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#666',
                      fontSize: { xs: '0.9rem', md: '1.1rem' },
                      mt: 1
                    }}
                  >
                    Gerencie os perfis de usuários do sistema
                  </Typography>
                </Box>
              </Grid>



              {/* Filtro de Status */}
              <Grid item xs={12}>
                <Box sx={{
                  backgroundColor: 'white',
                  borderRadius: 3,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  padding: { xs: 2, md: 3 },
                  mb: 2
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976D2' }}>
                      Filtrar por Status:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        variant={filters.status === 'ATIVO' ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => setFilters({ ...filters, status: 'ATIVO' })}
                        sx={{
                          backgroundColor: filters.status === 'ATIVO' ? '#4CAF50' : 'transparent',
                          color: filters.status === 'ATIVO' ? 'white' : '#4CAF50',
                          borderColor: '#4CAF50',
                          '&:hover': {
                            backgroundColor: filters.status === 'ATIVO' ? '#45a049' : 'rgba(76, 175, 80, 0.1)',
                            borderColor: '#45a049'
                          }
                        }}
                      >
                        <CheckCircle sx={{ mr: 1, fontSize: 16 }} />
                        Ativos
                      </Button>
                      <Button
                        variant={filters.status === 'INATIVO' ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => setFilters({ ...filters, status: 'INATIVO' })}
                        sx={{
                          backgroundColor: filters.status === 'INATIVO' ? '#f44336' : 'transparent',
                          color: filters.status === 'INATIVO' ? 'white' : '#f44336',
                          borderColor: '#f44336',
                          '&:hover': {
                            backgroundColor: filters.status === 'INATIVO' ? '#d32f2f' : 'rgba(244, 67, 54, 0.1)',
                            borderColor: '#d32f2f'
                          }
                        }}
                      >
                        <Cancel sx={{ mr: 1, fontSize: 16 }} />
                        Inativos
                      </Button>
                      <Button
                        variant={filters.status === 'Todos' ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => setFilters({ ...filters, status: 'Todos' })}
                        sx={{
                          backgroundColor: filters.status === 'Todos' ? '#1976D2' : 'transparent',
                          color: filters.status === 'Todos' ? 'white' : '#1976D2',
                          borderColor: '#1976D2',
                          '&:hover': {
                            backgroundColor: filters.status === 'Todos' ? '#1565C0' : 'rgba(25, 118, 210, 0.1)',
                            borderColor: '#1565C0'
                          }
                        }}
                      >
                        <Badge sx={{ mr: 1, fontSize: 16 }} />
                        Todos
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Grid>

              {/* Lista de perfis */}
              <Grid item xs={12}>
                <Box sx={{
                  backgroundColor: 'white',
                  borderRadius: 3,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  overflow: 'hidden'
                }}>
                  {perfis.length > 0 ? (
                    <>
                      {/* Tabela Desktop */}
                      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                        <TableContainer component={Paper} elevation={0} sx={{ width: '100%' }}>
                          <Table>
                            <TableHead sx={{ backgroundColor: '#1976D2' }}>
                              <TableRow>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '1rem' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Person />
                                    Nome
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '1rem' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Business />
                                    Empresa
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '1rem' }}>
                                  CNPJ
                                </TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '1rem' }}>
                                  IDPDV
                                </TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '1rem' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Phone />
                                    Telefone
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '1rem' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Email />
                                    Email
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '1rem' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AccessTime />
                                    Data/Hora
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '1rem' }}>
                                  Nível
                                </TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '1rem' }}>
                                  Status
                                </TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '1rem' }}>
                                  Ações
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {perfis.map((perfil, index) => (
                                <TableRow
                                  key={perfil._id}
                                  sx={{
                                    cursor: 'pointer',
                                    backgroundColor: index % 2 === 0 ? 'white' : '#F8F9FA',
                                    '&:hover': {
                                      backgroundColor: '#E3F2FD',
                                      transform: 'scale(1.01)',
                                    },
                                    transition: 'all 0.2s ease',
                                    borderBottom: '1px solid #E0E0E0'
                                  }}
                                >
                                  <TableCell
                                    sx={{ fontSize: '0.9rem', padding: '12px 16px' }}
                                    onClick={() => handlePerfilClick(perfil)}
                                  >
                                    {perfil.fullname ? perfil.fullname : '-'}
                                  </TableCell>
                                  <TableCell
                                    sx={{ fontSize: '0.9rem', padding: '12px 16px' }}
                                    onClick={() => handlePerfilClick(perfil)}
                                  >
                                    {perfil.empresa ? perfil.empresa : '-'}
                                  </TableCell>
                                  <TableCell
                                    sx={{ fontSize: '0.9rem', padding: '12px 16px' }}
                                    onClick={() => handlePerfilClick(perfil)}
                                  >
                                    {perfil.cnpj ? perfil.cnpj : '-'}
                                  </TableCell>
                                  <TableCell
                                    sx={{ fontSize: '0.9rem', padding: '12px 16px' }}
                                    onClick={() => handlePerfilClick(perfil)}
                                  >
                                    {perfil.idpdv ? perfil.idpdv : '-'}
                                  </TableCell>
                                  <TableCell
                                    sx={{ fontSize: '0.9rem', padding: '12px 16px' }}
                                    onClick={() => handlePerfilClick(perfil)}
                                  >
                                    {formatPhoneNumber('+' + perfil.numero)}
                                  </TableCell>
                                  <TableCell
                                    sx={{ fontSize: '0.9rem', padding: '12px 16px' }}
                                    onClick={() => handlePerfilClick(perfil)}
                                  >
                                    {perfil.email ? perfil.email : '-'}
                                  </TableCell>
                                  <TableCell
                                    sx={{ fontSize: '0.9rem', padding: '12px 16px' }}
                                    onClick={() => handlePerfilClick(perfil)}
                                  >
                                    {perfil.createdDate ? new Date(perfil.createdDate).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '-'}
                                  </TableCell>
                                  <TableCell sx={{ padding: '12px 16px' }}>
                                    <Typography variant="body2">{roleLabels[perfil.role] || perfil.role}</Typography>
                                  </TableCell>
                                  <TableCell sx={{ padding: '12px 16px' }}>
                                    <Chip
                                      label={perfil.status === 'ATIVO' ? 'ATIVO' :
                                        perfil.status === 'EMANALISE' ? 'EM ANÁLISE' :
                                          perfil.status === 'INATIVO' ? 'INATIVO' : 'BLOQUEADO'}
                                      color={perfil.status === 'ATIVO' ? 'success' :
                                        perfil.status === 'EMANALISE' ? 'info' : 'error'}
                                      size="small"
                                      icon={perfil.status === 'ATIVO' ? <CheckCircle /> :
                                        perfil.status === 'EMANALISE' ? <Pending /> : <Cancel />}
                                      onClick={canEditProfiles ? () => handleChangeStatus(perfil) : undefined}
                                      sx={{ cursor: canEditProfiles ? 'pointer' : 'default' }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ padding: '12px 16px' }} onClick={(e) => e.stopPropagation()}>
                                    {canEditProfiles && (
                                    <Box display="flex" flexWrap="nowrap" alignItems="center" gap={0.5}>
                                      <Tooltip title="Editar">
                                        <IconButton
                                          color="primary"
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleEdit(perfil);
                                          }}
                                        >
                                          <EditIcon />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title={perfil.status === 'INATIVO' ? 'Usuário já excluído' : 'Excluir'}>
                                        <span>
                                          <IconButton
                                            color="error"
                                            size="small"
                                            disabled={perfil.status === 'INATIVO'}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDelete(perfil._id);
                                            }}
                                            sx={{
                                              opacity: perfil.status === 'INATIVO' ? 0.3 : 1,
                                              cursor: perfil.status === 'INATIVO' ? 'not-allowed' : 'pointer'
                                            }}
                                          >
                                            <DeleteIcon />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    </Box>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>

                      <Modal
                        open={open}
                        onClose={handleClose}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Box
                          sx={{
                            position: 'fixed',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: { xs: '90%', sm: 450 },
                            maxWidth: { xs: '320px', sm: '450px' },
                            maxHeight: { xs: '85vh', sm: '90vh' },
                            overflow: 'auto',
                            bgcolor: 'background.paper',
                            borderRadius: 2,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                            p: { xs: 1.5, sm: 2, md: 3 },
                            border: '1px solid rgba(255,255,255,0.2)',
                            margin: 0,
                            zIndex: 1300,
                          }}
                        >
                          {/* Header */}
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: 1, sm: 1.5 },
                            mb: { xs: 1.5, sm: 2 },
                            pb: { xs: 1, sm: 1.5 },
                            borderBottom: '1px solid #E0E0E0'
                          }}>
                            <Person color="primary" sx={{ fontSize: { xs: 24, sm: 28 } }} />
                            <Typography
                              variant="h5"
                              component="h2"
                              sx={{
                                fontWeight: 700,
                                fontFamily: 'Ubuntu, sans-serif',
                                color: '#1976D2',
                                fontSize: { xs: '1.25rem', sm: '1.5rem' }
                              }}
                            >
                              Editar Perfil
                            </Typography>
                          </Box>

                          {/* Form Fields */}
                          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                            <Grid item xs={12} sm={6}>
                              <ModernTextField
                                label="Nome"
                                fullWidth
                                value={selectedPerfil?.fullname || ''}
                                onChange={(e) => setSelectedPerfil((prev) => ({ ...prev, fullname: e.target.value }))}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <Person color="action" />
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <ModernTextField
                                label="Empresa"
                                fullWidth
                                value={selectedPerfil?.empresa || ''}
                                onChange={(e) => setSelectedPerfil((prev) => ({ ...prev, empresa: e.target.value }))}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <Business color="action" />
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <ModernTextField
                                label="CNPJ"
                                fullWidth
                                value={selectedPerfil?.cnpj || ''}
                                onChange={(e) => setSelectedPerfil((prev) => ({ ...prev, cnpj: formatCNPJ(e.target.value) }))}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <Badge color="action" />
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <ModernTextField
                                label="Telefone"
                                fullWidth
                                value={selectedPerfil?.numero || ''}
                                onChange={(e) => setSelectedPerfil((prev) => ({ ...prev, numero: e.target.value }))}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <Phone color="action" />
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <ModernTextField
                                label="Email"
                                fullWidth
                                value={selectedPerfil?.email || ''}
                                disabled
                                helperText="Email não pode ser alterado pelo usuário. Contate o administrador."
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <Email color="action" />
                                    </InputAdornment>
                                  ),
                                  style: { backgroundColor: '#f5f5f5' }
                                }}
                              />
                            </Grid>

                            {currentUser?.role === 'SUPER_ADMIN' && selectedPerfil?.role !== 'SUPER_ADMIN' && (
                              <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Nível</Typography>
                                  <Select
                                    value={selectedPerfil?.role || 'USER'}
                                    onChange={(e) => setSelectedPerfil((prev) => ({ ...prev, role: e.target.value }))}
                                    displayEmpty
                                    sx={{ borderRadius: 2 }}
                                  >
                                    <MenuItem value="USER">{roleLabels.USER}</MenuItem>
                                    <MenuItem value="ADMIN">{roleLabels.ADMIN}</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                            )}

                            {currentUser?.role === 'SUPER_ADMIN' && (
                              <Grid item xs={12}>
                                <ModernTextField
                                  label="Sua senha para confirmar alterações"
                                  type="password"
                                  fullWidth
                                  value={passwordConfirm}
                                  onChange={(e) => setPasswordConfirm(e.target.value)}
                                  placeholder={initialPerfilRef.current?.role !== selectedPerfil?.role ? 'Obrigatório ao alterar o nível' : 'Opcional'}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <Lock color="action" />
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Grid>
                            )}
                          </Grid>

                          {/* Action Buttons */}
                          <Box sx={{
                            display: 'flex',
                            gap: { xs: 1, sm: 1.5 },
                            justifyContent: 'flex-end',
                            mt: { xs: 2, sm: 3 },
                            pt: { xs: 1, sm: 1.5 },
                            borderTop: '1px solid #E0E0E0'
                          }}>
                            <Button
                              variant="outlined"
                              color="secondary"
                              onClick={handleClose}
                              sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 3
                              }}
                            >
                              Cancelar
                            </Button>
                            <ModernButton onClick={handleSave}>
                              Salvar
                            </ModernButton>
                          </Box>
                        </Box>
                      </Modal>

                      {/* Cards Mobile */}
                      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                        {perfis.map((perfil) => (
                          <ModernCard
                            key={perfil._id}
                            sx={{
                              width: '100%',
                              marginBottom: 2,
                              cursor: 'pointer'
                            }}
                            onClick={() => handlePerfilClick(perfil)}
                          >
                            <CardContent sx={{ padding: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                <Chip
                                  label={perfil.status === 'ATIVO' ? 'ATIVO' :
                                    perfil.status === 'EMANALISE' ? 'EM ANÁLISE' :
                                      perfil.status === 'INATIVO' ? 'INATIVO' : 'BLOQUEADO'}
                                  color={perfil.status === 'ATIVO' ? 'success' :
                                    perfil.status === 'EMANALISE' ? 'info' : 'error'}
                                  size="small"
                                  icon={perfil.status === 'ATIVO' ? <CheckCircle /> :
                                    perfil.status === 'EMANALISE' ? <Pending /> : <Cancel />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleChangeStatus(perfil);
                                  }}
                                />
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976D2' }}>
                                  {perfil.fullname || '-'}
                                </Typography>
                              </Box>

                              <Divider sx={{ my: 1 }} />

                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Business color="action" fontSize="small" />
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {perfil.empresa || '-'}
                                  </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Phone color="action" fontSize="small" />
                                  <Typography variant="body2">
                                    {formatPhoneNumber('+' + perfil.numero)}
                                  </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Email color="action" fontSize="small" />
                                  <Typography variant="body2">
                                    {perfil.email || '-'}
                                  </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <AccessTime color="action" fontSize="small" />
                                  <Typography variant="body2">
                                    {perfil.createdDate ? new Date(perfil.createdDate).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                                  </Typography>
                                </Box>
                                {currentUser?.role === 'SUPER_ADMIN' && (
                                  <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #eee' }}>
                                    <Typography variant="caption" color="text.secondary">Nível: {roleLabels[perfil.role] || perfil.role}</Typography>
                                  </Box>
                                )}
                              </Box>

                              {canEditProfiles && (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                                  <Tooltip title="Editar">
                                    <IconButton
                                      color="primary"
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEdit(perfil);
                                      }}
                                    >
                                      <EditIcon />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Excluir">
                                    <IconButton
                                      color="error"
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(perfil._id);
                                      }}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              )}
                            </CardContent>
                          </ModernCard>
                        ))}
                      </Box>

                    </>
                  ) : (
                    <Box sx={{
                      textAlign: 'center',
                      padding: 4,
                      backgroundColor: 'rgba(0,0,0,0.05)',
                      borderRadius: 2
                    }}>
                      <Typography variant="h6" color="text.secondary">
                        Nenhum perfil encontrado
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>

              {/* Paginação */}
              <Grid item xs={12}>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: 3,
                  backgroundColor: 'white',
                  borderRadius: 3,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  padding: 2
                }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                    renderItem={(item) => (
                      <PaginationItem
                        component="button"
                        {...item}
                        onClick={(event) => handlePageChange(event, item.page)}
                      />
                    )}
                  />
                </Box>
              </Grid>
            </Grid>
          )}
        </Container>
      </Box>
    </ThemeProvider>
  );

}

